import React, { useMemo, useState } from 'react';

import {
    Checkbox,
    FormControl,
    InputLabel,
    MenuItem,
    Select
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

import './multiSelectFilter.css';

const buildMenuProps = (blur) => ({
    PaperProps: {
        className: blur
            ? 'multi-select-filter-menu-paper'
            : 'multi-select-filter-menu-paper multi-select-filter-menu-paper-no-blur'
    },
    MenuListProps: {
        className: 'multi-select-filter-menu-list'
    },
    BackdropProps: blur ? undefined : { className: 'multi-select-filter-backdrop-no-blur' }
});

const normalizeValues = (value, allOptionValue) => {
    if (!Array.isArray(value) || value.length === 0) return [allOptionValue];
    return value;
};

const MultiSelectFilter = ({
    allOptionValue = 'all',
    ariaLabel,
    blur = true,
    className = '',
    disabled = false,
    label,
    labelId,
    onChange,
    onClose,
    options,
    size,
    value
}) => {
    const [isCleared, setIsCleared] = useState(false);
    const selectedValues = normalizeValues(value, allOptionValue);
    const menuProps = useMemo(() => buildMenuProps(blur), [blur]);
    const optionLabelByValue = useMemo(
        () => new Map(options.map((option) => [option.value, option.label])),
        [options]
    );
    const groupOptions = useMemo(
        () => options.filter((option) => Array.isArray(option.childValues)),
        [options]
    );
    const nonAllOptionValues = useMemo(
        () => options
            .filter((option) => !Array.isArray(option.childValues))
            .map((option) => option.value)
            .filter((optionValue) => optionValue !== allOptionValue),
        [allOptionValue, options]
    );
    const isEverythingSelected = !isCleared && selectedValues.includes(allOptionValue);
    const effectiveValues = isCleared ? [] : isEverythingSelected ? nonAllOptionValues : selectedValues;
    const displayValues = isCleared ? [] : isEverythingSelected ? [allOptionValue, ...nonAllOptionValues] : selectedValues;

    const getGroupState = (group) => {
        const selectedCount = group.childValues.filter((childValue) => effectiveValues.includes(childValue)).length;
        return {
            checked: selectedCount > 0 && selectedCount === group.childValues.length,
            indeterminate: selectedCount > 0 && selectedCount < group.childValues.length
        };
    };

    const renderValue = (selected) => {
        if (selected.includes(allOptionValue) || selected.length === 0) {
            return optionLabelByValue.get(allOptionValue);
        }

        const coveredValues = new Set();
        const labels = [];

        groupOptions.forEach((group) => {
            if (!group.childValues.every((childValue) => selected.includes(childValue))) return;
            labels.push(group.label);
            group.childValues.forEach((childValue) => coveredValues.add(childValue));
        });

        selected.forEach((selectedValue) => {
            if (coveredValues.has(selectedValue)) return;
            const selectedLabel = optionLabelByValue.get(selectedValue);
            if (selectedLabel) labels.push(selectedLabel);
        });

        return labels.join(', ');
    };

    const emitValues = (nextValuesWithoutAll) => {
        setIsCleared(nextValuesWithoutAll.length === 0);

        if (nextValuesWithoutAll.length === 0 || nextValuesWithoutAll.length === nonAllOptionValues.length) {
            onChange([allOptionValue]);
            return;
        }

        onChange(nextValuesWithoutAll);
    };

    const handleClose = (event) => {
        setIsCleared(false);
        if (onClose) onClose(event);
    };

    const handleChange = (event) => {
        const rawValue = event.target.value;
        const nextValues = Array.isArray(rawValue) ? rawValue : String(rawValue).split(',');
        const wasAllSelected = isEverythingSelected;
        const isAllSelected = nextValues.includes(allOptionValue);
        const clickedGroup = groupOptions.find((group) => nextValues.includes(group.value));

        if (clickedGroup) {
            const currentValues = effectiveValues;
            const isGroupComplete = clickedGroup.childValues.every((childValue) => currentValues.includes(childValue));
            const nextGroupValues = isGroupComplete
                ? currentValues.filter((currentValue) => !clickedGroup.childValues.includes(currentValue))
                : [
                    ...currentValues,
                    ...clickedGroup.childValues.filter((childValue) => !currentValues.includes(childValue))
                ];
            emitValues(nextGroupValues);
            return;
        }

        const nextValuesWithoutAll = nextValues.filter((nextValue) => nextValue !== allOptionValue);

        if (isAllSelected && !wasAllSelected) {
            setIsCleared(false);
            onChange([allOptionValue]);
            return;
        }

        if (wasAllSelected && !isAllSelected) {
            emitValues([]);
            return;
        }

        emitValues(nextValuesWithoutAll);
    };

    const renderOption = (option) => {
        if (Array.isArray(option.childValues)) {
            const groupState = getGroupState(option);
            return (
                <MenuItem
                    className={`multi-select-filter-menu-item multi-select-filter-menu-item-group${groupState.checked ? ' multi-select-filter-menu-item-group-selected' : ''}`}
                    key={option.value}
                    value={option.value}
                >
                    <Checkbox
                        className='multi-select-filter-checkbox'
                        checked={groupState.checked}
                        indeterminate={groupState.indeterminate}
                    />
                    <span>{option.label}</span>
                </MenuItem>
            );
        }

        return (
            <MenuItem
                className={`multi-select-filter-menu-item${option.nested ? ' multi-select-filter-menu-item-nested' : ''}`}
                key={option.value}
                value={option.value}
            >
                <Checkbox className='multi-select-filter-checkbox' checked={displayValues.includes(option.value)} />
                <span>{option.label}</span>
            </MenuItem>
        );
    };

    return (
        <FormControl className={className} size={size}>
            {label && <InputLabel id={labelId}>{label}</InputLabel>}
            <Select
                labelId={labelId}
                label={label}
                value={displayValues}
                disabled={disabled}
                inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
                multiple
                displayEmpty
                renderValue={renderValue}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuProps}
                onChange={handleChange}
                onClose={handleClose}
            >
                {options.map(renderOption)}
            </Select>
        </FormControl>
    );
};

export default MultiSelectFilter;
